/**
 * Agent runtime URL configuration.
 *
 * REACT_APP_AGENT_RUNTIME_BASE_URL — optional dedicated agent host (local or remote).
 * REACT_APP_API_BASE_URL — JB/CIP gateway base (e.g. …/cip/api/v2 or full …/cip/api/v2/agent/configure).
 * REACT_APP_AGENT_RUNTIME_PUBLIC_URL — URL CIX/journeys use to invoke agents (defaults to runtime base).
 *
 * Env values are used as configured (no trailing-slash trimming). Configure URL example:
 * https://jb.azdemo.cloudintelligence.industrycloud.pwc.com/cip/api/v2/agent/configure
 *
 * Services/events use the same gateway with a /cix prefix when CP env vars are unset:
 * …/cip/api/v2/cix/services/search, …/cip/api/v2/cix/events/search
 */

const AGENT_CONFIGURE_SUFFIX = /\/agent\/configure\/?$/i;
const CIX_API_SUFFIX = /\/cix\/?$/i;
const CIP_API_V2_SUFFIX = /\/cip\/api\/v2\/?$/i;

const readEnv = (key: string): string => {
  const value = process.env[key];
  return value ? String(value) : '';
};

/** Runtime host root (configure suffix removed when env is a full configure URL). */
export const getAgentRuntimeBaseUrl = (): string => {
  const raw = readEnv('REACT_APP_AGENT_RUNTIME_BASE_URL') || readEnv('REACT_APP_API_BASE_URL');
  if (!raw) return '';
  if (AGENT_CONFIGURE_SUFFIX.test(raw)) {
    return raw.replace(AGENT_CONFIGURE_SUFFIX, '');
  }
  return raw;
};

/**
 * CIX API root for services/events ({@link REACT_APP_API_CONTROL_PLANE_GET_ALL_SERVICE} /
 * {@link REACT_APP_API_CONTROL_PLANE_BASE_URL}, or derived from the agent gateway base).
 */
export const getControlPlaneApiBaseUrl = (): string => {
  const explicit = readEnv('REACT_APP_API_CONTROL_PLANE_GET_ALL_SERVICE')
    || readEnv('REACT_APP_API_CONTROL_PLANE_BASE_URL');
  if (explicit) return explicit;

  const runtime = getAgentRuntimeBaseUrl();
  if (!runtime) return '';

  if (CIX_API_SUFFIX.test(runtime)) return runtime;
  if (CIP_API_V2_SUFFIX.test(runtime)) {
    return runtime.endsWith('/') ? `${runtime}cix` : `${runtime}/cix`;
  }
  return runtime;
};

export const getAgentRuntimePublicUrl = (): string =>
  readEnv('REACT_APP_AGENT_RUNTIME_PUBLIC_URL')
  || getAgentRuntimeBaseUrl();

/**
 * Gateway root for agent runtime APIs (agent/register-agent, agent/configure, debug, knowledge) — no /cix segment.
 * Example: …/cip/api/v2/cix → …/cip/api/v2
 */
export const buildAgentApiBaseUrl = (baseUrl: string): string => {
  if (!baseUrl) return '/cip/api/v2';

  if (AGENT_CONFIGURE_SUFFIX.test(baseUrl)) {
    return baseUrl.replace(AGENT_CONFIGURE_SUFFIX, '');
  }

  if (CIX_API_SUFFIX.test(baseUrl)) {
    return baseUrl.replace(CIX_API_SUFFIX, '');
  }

  if (CIP_API_V2_SUFFIX.test(baseUrl)) {
    return baseUrl;
  }

  if (baseUrl.endsWith('/api/agent/configure')) return baseUrl.replace(AGENT_CONFIGURE_SUFFIX, '');
  if (baseUrl.endsWith('/api/agent')) return baseUrl.replace(/\/agent\/?$/, '');
  if (baseUrl.endsWith('/api')) return baseUrl;
  return baseUrl;
};

/** Builds …/cip/api/v2/agent/configure (or returns env when already a configure URL). */
export const buildAgentConfigureUrl = (baseUrl: string): string => {
  if (!baseUrl) return '/cip/api/v2/agent/configure';

  if (AGENT_CONFIGURE_SUFFIX.test(baseUrl)) return baseUrl;

  if (CIP_API_V2_SUFFIX.test(baseUrl)) {
    return baseUrl.endsWith('/') ? `${baseUrl}agent/configure` : `${baseUrl}/agent/configure`;
  }

  if (CIX_API_SUFFIX.test(baseUrl)) {
    const root = baseUrl.replace(CIX_API_SUFFIX, '');
    return root.endsWith('/') ? `${root}agent/configure` : `${root}/agent/configure`;
  }

  // Legacy local agent-backend (…/api/agent/configure)
  if (baseUrl.endsWith('/api/agent/configure')) return baseUrl;
  if (baseUrl.endsWith('/api/agent')) return `${baseUrl}/configure`;
  if (baseUrl.endsWith('/api')) return `${baseUrl}/agent/configure`;
  return `${baseUrl}/api/agent/configure`;
};

export const getAgentConfigureUrl = (): string => {
  const raw = readEnv('REACT_APP_AGENT_RUNTIME_BASE_URL') || readEnv('REACT_APP_API_BASE_URL');
  if (raw && AGENT_CONFIGURE_SUFFIX.test(raw)) return raw;
  return buildAgentConfigureUrl(getAgentRuntimeBaseUrl() || raw);
};
